// frontend/src/components/aria/AriaCommandBar.js
import { useState, useEffect, useRef } from 'react';
import { useAriaContext } from '../../contexts/AriaContext';
import api from '../../api';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const TYPE_COLORS = {
  navigate: 'hsl(200,80%,55%)',
  action: 'hsl(75,100%,50%)',
  ask_aria: 'hsl(280,65%,60%)',
  search: 'hsl(0,0%,50%)',
};

const S = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.75)', display: 'flex',
    alignItems: 'flex-start', justifyContent: 'center', paddingTop: 120,
  },
  box: {
    width: '100%', maxWidth: 560,
    background: 'hsl(0,0%,5%)', border: '1px solid hsl(75,100%,50%)',
    overflow: 'hidden',
  },
  label: { ...MONO, fontSize: 8, color: 'hsl(75,100%,50%)', textTransform: 'uppercase', letterSpacing: '.1em', padding: '8px 12px 0', display: 'block' },
  input: {
    width: '100%', background: 'transparent', border: 'none',
    borderBottom: '1px solid hsl(0,0%,12%)',
    padding: '10px 12px', color: 'hsl(0,0%,90%)',
    ...MONO, fontSize: 12, outline: 'none', boxSizing: 'border-box',
  },
  results: { maxHeight: 280, overflowY: 'auto' },
  result: (active) => ({
    padding: '8px 12px', cursor: 'pointer', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
    background: active ? 'hsl(0,0%,9%)' : 'transparent',
    borderBottom: '1px solid hsl(0,0%,8%)',
  }),
  resultLabel: { ...MONO, fontSize: 10, color: 'hsl(0,0%,75%)' },
  resultType: (type) => ({
    ...MONO, fontSize: 8, color: TYPE_COLORS[type] || 'hsl(0,0%,40%)',
    textTransform: 'uppercase', letterSpacing: '.08em',
  }),
  hint: { ...MONO, fontSize: 8, color: 'hsl(0,0%,25%)', padding: '6px 12px', textAlign: 'center' },
};

export default function AriaCommandBar() {
  const { commandBarOpen, closeCommandBar, openPanel } = useAriaContext();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (commandBarOpen) {
      setQuery('');
      setResults([]);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandBarOpen]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      try {
        const { data } = await api.get('/aria/suggest', { params: { q: query } });
        setResults(data);
        setActiveIdx(0);
      } catch { setResults([]); }
    }, 120);
    return () => clearTimeout(timeout);
  }, [query]);

  const execute = (result) => {
    closeCommandBar();
    if (result.type === 'ask_aria') {
      openPanel();
      return;
    }
    if (result.type === 'navigate') {
      const hl = result.payload?.highlight;
      const url = hl
        ? `${result.target_path}?highlight=${encodeURIComponent(hl)}`
        : result.target_path;
      window.open(url, '_blank');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[activeIdx]) execute(results[activeIdx]);
    if (e.key === 'Escape') closeCommandBar();
  };

  if (!commandBarOpen) return null;

  return (
    <div style={S.backdrop} onClick={closeCommandBar}>
      <div style={S.box} onClick={e => e.stopPropagation()}>
        <span style={S.label}>ARIA COMMAND · ESC TO CLOSE</span>
        <input
          ref={inputRef}
          style={S.input}
          placeholder="Search or give a command..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          data-testid="aria-command-input"
        />
        <div style={S.results}>
          {results.map((r, i) => (
            <div
              key={i}
              style={S.result(i === activeIdx)}
              onClick={() => execute(r)}
              onMouseEnter={() => setActiveIdx(i)}
            >
              <span style={S.resultLabel}>{r.label}</span>
              <span style={S.resultType(r.type)}>{r.type}</span>
            </div>
          ))}
        </div>
        <div style={S.hint}>↑↓ navigate · Enter to select · complex requests open ARIA panel</div>
      </div>
    </div>
  );
}
